import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

const HIGGINS_PURPLE = '#8b189b'; // Higgins brand purple (PMS 2602C)

export interface SidePanelProps {
  /** Controlled open state. Leave unset (with `trigger`) to let the panel manage it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the panel when pressed (rendered as the Radix trigger). */
  trigger?: ReactNode;
  /** Heading at the top of the panel. */
  title: ReactNode;
  /** Screen-reader description of the panel. */
  description: string;
  /** Accessible label of the close button. Defaults to "Close". */
  closeLabel?: string;
  children: ReactNode;
}

/**
 * Right-hand slide-over: full width on a phone, `max-w-md` from there up.
 * Used by `HelpPanel`; an app opens its own content in it, controlled with
 * `open` / `onOpenChange`.
 */
export function SidePanel({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  closeLabel = 'Close',
  children,
}: SidePanelProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>}

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in data-[state=closed]:fade-out" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-md flex-col bg-background shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right"
        >
          <div
            className="flex items-center justify-between border-b px-5 py-4"
            style={{ borderColor: HIGGINS_PURPLE }}
          >
            <DialogPrimitive.Title className="text-lg font-semibold tracking-tight">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label={closeLabel}
              className="rounded-sm opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:ring-2"
            >
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="sr-only">
            {description}
          </DialogPrimitive.Description>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
